// Shared GLSL for the premium WebGL foil. Prototype lives on the card
// inspector; see docs/plans/foil-webgl-shader.md. All look parameters are
// uniforms (u<Key>) driven by foilParams.js so the dev panel can tune live.
//
// Realism stack: parallax-occlusion relief, procedural studio-environment
// reflection, GGX/Cook-Torrance metallic specular with Schlick Fresnel,
// luminance-keyed metal (light areas = foil, dark = matte), iridescence,
// directional sparkle, zone emboss. Static at rest, motion only on tilt.

export const FOIL_VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  // Tangent-space view + light directions for the board (uSceneLit=1). The
  // front cap's UV maps U->local +X, V->local +Y, face normal->local +Z (see
  // cardMesh generateTopUV), so the card's tangent frame in world space is the
  // model matrix applied to those local axes. We pass V and L already expressed
  // in that tangent basis so the fragment shader can light the relief normal N
  // (also z-up tangent space) directly, exactly like the DOM path. Only now
  // the basis tracks the REAL camera + scene light. Unused when uSceneLit=0.
  varying vec3 vViewTS;
  varying vec3 vLightTS;
  uniform vec3 uLightDir;   // world-space direction TOWARD the light, normalized
  void main() {
    vUv = uv;
    vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    vec3 T = normalize(mat3(modelMatrix) * vec3(1.0, 0.0, 0.0));
    vec3 B = normalize(mat3(modelMatrix) * vec3(0.0, 1.0, 0.0));
    vec3 Nw = normalize(mat3(modelMatrix) * vec3(0.0, 0.0, 1.0));
    vec3 viewW = normalize(cameraPosition - worldPos);
    vViewTS = vec3(dot(viewW, T), dot(viewW, B), dot(viewW, Nw));
    vec3 lW = normalize(uLightDir);
    vLightTS = vec3(dot(lW, T), dot(lW, B), dot(lW, Nw));
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const FOIL_FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  varying vec2 vUv;
  // Board scene-lit varyings (computed in the vertex shader from the real
  // camera + uLightDir). Used only when uSceneLit > 0.5; harmless otherwise.
  varying vec3 vViewTS;
  varying vec3 vLightTS;
  uniform float uSceneLit;       // 0 = DOM self-lit (default), 1 = board scene-lit
  uniform vec3 uLightColor;      // scene directional light rgb * intensity
  uniform vec3 uAmbient;         // scene ambient rgb * intensity
  uniform sampler2D uTex;
  uniform sampler2D uMatcap;
  uniform sampler2D uFoilMap;   // baked RG=normal grad, B=height, A=white mask
  uniform float uHasFoilMap;    // 1 if the baked map is loaded, else derive
  uniform sampler2D uDepthMap;  // baked monocular depth, R = near(1)..far(0)
  uniform float uHasDepthMap;   // 1 if the baked depth map is loaded, else estimate
  uniform vec2 uTexel;
  uniform vec2 uTilt;
  uniform float uFromCenter;
  uniform float uTime;
  uniform vec4 uZone0;
  uniform vec4 uZone1;

  // Tunables (foilParams.js)
  uniform float uPomOn, uPomDepth, uReliefStrength, uNormalBlur;
  uniform float uDepthReliefStrength, uDepthShine;
  uniform float uEnvOn, uEnvStr, uMetalTint;
  uniform float uMatcapOn, uMatcapRot;
  uniform float uGgxOn, uGgxStr, uRough;
  uniform float uIridOn, uIridStr, uIridTint, uHueShift, uDeckRamp;
  uniform float uThinFilmOn, uFilmThickness, uFilmIor;
  uniform float uSparkleOn, uSparkleStr, uSparkleDensity, uSparkleThresh;
  uniform float uGlintOn, uGlintDensity, uGlintSharp, uGlintSpread, uGlintBrightness;
  uniform float uEmbossOn, uEmbossStr;
  uniform float uShineLo, uShineHi, uRestFloor, uShadowStr;
  uniform float uDarkShineLo, uDarkShineHi, uColorThresh;
  uniform float uRestLightX, uRestLightY;
  uniform float uRestHolo;
  // Rounded-corner coverage (UV-fraction radius). 0 = no rounding (default,
  // used by the inspector / hand / board where the DOM clips the canvas). The
  // shared FoilStage sets this > 0 because its single canvas spans the whole
  // grid viewport and can't rely on per-tile CSS border-radius to clip quads.
  uniform float uCornerRadius;
  // Quad height / width. The corner SDF runs in width units so the radius
  // is circular on screen (in raw UV a portrait quad rounds taller than wide).
  uniform float uQuadAspect;
  uniform float uUseTexAlpha;
  // Whole-quad fade. Lets a caller cross-fade an individual foil card in or out
  // WITH its foil effect intact, instead of fading a separate layer over it.
  // Defaults to 1 everywhere; only the deck-builder grid's entrance drives it.
  uniform float uOpacity;
  // Object-cover sampling crop (CHANGE 1): the stage quad's aspect can differ
  // from the card art's aspect (landscape sites in a portrait tile box). The
  // DOM <img> uses object-cover (center-crop); to match, the shader samples a
  // cropped sub-rect of the texture: uvc = vUv * uUvScale + uUvOffset. Default
  // (1,1)/(0,0) is an identity transform (uvc == vUv), so portrait cards and
  // every non-stage caller are byte-identical to before.
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  // Site rotation: site art is stored portrait but displayed rotated 90deg CW
  // (so it reads horizontally), matching the DOM img transform rotate(90deg).
  // The grid (CardFoilScene) rotates its quad mesh instead, so it leaves this 0;
  // the single-quad surfaces (inspector + hover previews) keep their inset:0
  // canvas and set this 1, rotating the SAMPLED texture so the foil overlay
  // matches the rotated image underneath instead of showing portrait art sideways.
  uniform float uRotate90;
  // Board parity: 1 = sRGB-decode the final color to cancel the scene
  // renderer's sRGB output-encode (so the raw foil math reaches screen exactly
  // like the deck builder). DOM callers leave 0 (they output LinearSRGB).
  uniform float uOutputLinear;

  const float PI = 3.14159265;

  float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
  vec2 imgSpace(vec2 uv) { return vec2(uv.x, 1.0 - uv.y); }
  float zoneSDF(vec2 p, vec4 r) {
    vec2 c = r.xy + r.zw * 0.5;
    vec2 d = abs(p - c) - r.zw * 0.5;
    return max(d.x, d.y);
  }
  vec3 metallicRamp(float t) {
    return 0.5 + 0.38 * cos(6.28318 * (t + vec3(0.00, 0.18, 0.42)));
  }
  // Push a colour's saturation (k>1 = more vivid) around its own luminance.
  vec3 satBoost(vec3 c, float k) {
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    return clamp(mix(vec3(l), c, k), 0.0, 1.0);
  }

  // The exact 5-colour holo ramp from the legacy deck-builder CSS gradient
  // (#f80e35 #eedf10 #21e985 #0dbde9 #c929f1), cycled. Using these instead of
  // a cosine ramp so the WebGL colours match the deck builder.
  vec3 rainbow5(float t) {
    t = fract(t) * 5.0;
    float f = fract(t);
    vec3 c0 = vec3(0.973, 0.055, 0.208);
    vec3 c1 = vec3(0.933, 0.875, 0.063);
    vec3 c2 = vec3(0.129, 0.914, 0.522);
    vec3 c3 = vec3(0.051, 0.741, 0.914);
    vec3 c4 = vec3(0.788, 0.161, 0.945);
    if (t < 1.0) return mix(c0, c1, f);
    if (t < 2.0) return mix(c1, c2, f);
    if (t < 3.0) return mix(c2, c3, f);
    if (t < 4.0) return mix(c3, c4, f);
    return mix(c4, c0, f);
  }

  // Physically-based thin-film interference (Belcour-Barla style practical
  // approximation, the model standardized as glTF KHR_materials_iridescence).
  // A thin dielectric film of refractive index filmIor and thickness (nm) sits
  // on the metal. Light reflecting off the top and bottom interfaces is phase-
  // shifted by the extra optical path 2*filmIor*thickness*cosThetaFilm; that
  // phase, evaluated at three wavelengths (R/G/B), sets which colours
  // constructively interfere. We sum two-beam fringes per channel and tonemap
  // to stay in gamut.
  vec3 thinFilm(float cosTheta, float thickness, float filmIor) {
    cosTheta = clamp(cosTheta, 0.02, 1.0);
    // Refraction into the film (ambient IOR ~1.0) via Snell, then cos inside.
    float sinTheta2 = (1.0 - cosTheta * cosTheta) / (filmIor * filmIor);
    float cosFilm = sqrt(max(1.0 - sinTheta2, 0.0));
    // Optical path difference (nm). x2 = two passes through the film.
    float opd = 2.0 * filmIor * thickness * cosFilm;
    // Phase per channel for R/G/B reference wavelengths (nm) + a fixed pi shift
    // at the denser-medium interface.
    vec3 lambda = vec3(615.0, 535.0, 465.0);
    vec3 phase = (6.28318 * opd) / lambda + PI;
    // Two-beam interference intensity: 0.5*(1 - cos) maps to [0,1] per channel.
    vec3 irid = 0.5 - 0.5 * cos(phase);
    // Slight desaturation toward the mean so no single channel clamps hard,
    // keeping the result smooth and in gamut.
    float m = dot(irid, vec3(0.3333));
    return clamp(mix(vec3(m), irid, 0.85), 0.0, 1.0);
  }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec2 hash2(vec2 p) {
    return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453);
  }

  float heightAt(vec2 uv) {
    // Height for POM is derived live from luminance (cheap, 1 tap). The foil
    // map's B channel now holds the white-mask, not height.
    float l = luma(texture2D(uTex, uv).rgb);
    vec2 ip = imgSpace(uv);
    float zb = smoothstep(0.004, -0.012, min(zoneSDF(ip, uZone0), zoneSDF(ip, uZone1)));
    return l * 0.7 + zb * 0.3;
  }

  vec2 parallaxUV(vec2 uv, vec2 viewDir) {
    const int STEPS = 10;
    vec2 delta = viewDir * uPomDepth / float(STEPS);
    float layerStep = 1.0 / float(STEPS);
    float curLayer = 0.0;
    vec2 cur = uv;
    float h = heightAt(cur);
    for (int i = 0; i < STEPS; i++) {
      if (curLayer >= 1.0 - h) break;
      cur -= delta;
      h = heightAt(cur);
      curLayer += layerStep;
    }
    return cur;
  }

  float D_GGX(float ndh, float rough) {
    float a = rough * rough;
    float a2 = a * a;
    float d = ndh * ndh * (a2 - 1.0) + 1.0;
    return a2 / (PI * d * d);
  }
  float G_Smith(float ndl, float ndv, float rough) {
    float k = rough * rough * 0.5;
    float gl = ndl / (ndl * (1.0 - k) + k);
    float gv = ndv / (ndv * (1.0 - k) + k);
    return gl * gv;
  }

  vec3 studioEnv(vec2 p) {
    vec2 key = p - vec2(0.35, 0.55);
    vec2 fill = p + vec2(0.45, 0.25);
    float kl = exp(-6.0 * dot(key, key));
    float fl = exp(-9.0 * dot(fill, fill));
    float band = smoothstep(0.2, 1.0, p.y) * 0.22;
    return vec3(0.03) + vec3(1.0, 0.97, 0.90) * kl * 1.1 + vec3(0.6, 0.75, 1.0) * (fl * 0.7 + band);
  }

  void main() {
    // Object-cover sampling coordinate (CHANGE 1). uvc crops the texture to the
    // quad aspect; default uUvScale=(1,1)/uUvOffset=(0,0) makes uvc == vUv, so
    // non-stage callers and portrait cards are unchanged. Every card-art /
    // foilmap / Sobel / POM / zone sample below flows from uv0 = uvc, so the
    // whole effect crops together. (The rounded-corner coverage at the end
    // stays on raw vUv. That is geometry coverage, not a texture sample.)
    // Rotate the sampling coord 90deg CW for sites (screen-right shows image-top)
    // BEFORE the object-cover crop, so the portrait texture fills the landscape
    // quad rotated, exactly like the rotated DOM <img>. Identity when uRotate90=0.
    vec2 ruv = (uRotate90 > 0.5) ? vec2(1.0 - vUv.y, vUv.x) : vUv;
    vec2 uvc = ruv * uUvScale + uUvOffset;
    vec2 uv0 = uvc;

    // The foil DEPTH is expressed through the lighting + holographic sheen, NOT
    // by displacing the art (displacing a flat texture smears the disoccluded
    // band behind the subject. There is no hidden data to reveal). So the card
    // pixels stay pin-sharp here; depth feeds the relief normal (below) and the
    // holo slide so the shine wraps the subject's real 3D form and the sheen
    // parallaxes above the surface, like a real foil's refractive layer.
    vec2 puv = uv0;
    if (uPomOn > 0.5) {
      vec2 pp = parallaxUV(uv0, uTilt);
      // Keep the text zones (name plate + rules box) undisplaced so the text
      // stays sharp; they still read as raised via the bevel normal (lighting,
      // not a UV shift, so no blur).
      vec2 ip0 = imgSpace(uv0);
      float inZone = smoothstep(0.004, -0.02, min(zoneSDF(ip0, uZone0), zoneSDF(ip0, uZone1)));
      puv = mix(pp, uv0, inZone);
    }

    vec3 base = texture2D(uTex, puv).rgb;
    float Lm = luma(base);

    // White/foil substrate mask (near-white, desaturated): drives the text-zone
    // foil gate + the white->rainbow recolour. Derived at runtime from the art
    // so it stays independent of the baked COVERAGE mask now packed in foilmap.b.
    float satv = max(max(base.r, base.g), base.b) - min(min(base.r, base.g), base.b);
    float whiteMask = smoothstep(0.80, 0.96, Lm) * (1.0 - smoothstep(0.08, 0.28, satv));

    // Relief normal gradient (lx, ly) from the baked map (precomputed, amplified)
    // or a runtime Sobel. The baked map B channel carries this card's per-card
    // FOIL MODE (one constant), chosen by the classifier in generate-foil-maps.js:
    //   0.0 = LIGHT  (card has bright art -> foil the bright areas)
    //   0.5 = DARK   (card is dark        -> foil only the darkest tail)
    //   1.0 = COLOUR (card is flat        -> foil by colour contrast)
    float lx, ly, foilMode;
    if (uHasFoilMap > 0.5) {
      // 5-tap blur of the baked normal so the relief reads as smooth depth,
      // not a chisel on every leaf. uNormalBlur sets the radius (texels).
      vec2 nb = uTexel * uNormalBlur;
      vec2 g = texture2D(uFoilMap, puv).rg
             + texture2D(uFoilMap, puv + vec2(nb.x, 0.0)).rg
             + texture2D(uFoilMap, puv - vec2(nb.x, 0.0)).rg
             + texture2D(uFoilMap, puv + vec2(0.0, nb.y)).rg
             + texture2D(uFoilMap, puv - vec2(0.0, nb.y)).rg;
      g /= 5.0;
      lx = ((g.x - 0.5) * 2.0) / 4.0;    // decode: matches the bake NORMAL_GAIN = 4
      ly = ((g.y - 0.5) * 2.0) / 4.0;
      foilMode = texture2D(uFoilMap, puv).b;  // per-card foil mode (constant)
    } else {
      float s = 1.5;
      lx = luma(texture2D(uTex, puv + vec2(uTexel.x * s, 0.0)).rgb) - luma(texture2D(uTex, puv - vec2(uTexel.x * s, 0.0)).rgb);
      ly = luma(texture2D(uTex, puv + vec2(0.0, uTexel.y * s)).rgb) - luma(texture2D(uTex, puv - vec2(0.0, uTexel.y * s)).rgb);
      foilMode = 0.0;  // no baked mode -> default LIGHT
    }
    // Run EXACTLY ONE mode's rule per card (decided per-card by the classifier),
    // so coverage follows the art sparsely and modes never stack into a sheet.
    // Each rule's thresholds stay live uniforms; only the classification is baked.
    float shine;
    if (foilMode > 0.66) {
      // COLOUR: foil the STRONGEST-colour regions. The most saturated/vivid
      // pixels (Atlas Wanderers' red orb, the teal). And leave the muddy,
      // desaturated bulk matte. satv is per-pixel chroma (max-min), so this is
      // robust and self-limiting: only genuinely colourful areas foil, never the
      // whole card. colorThresh = the saturation a pixel needs before it foils.
      shine = smoothstep(uColorThresh, uColorThresh + 0.25, satv);
    } else if (foilMode > 0.33) {
      // DARK: a tight band near black, so only the darkest linework foils.
      shine = 1.0 - smoothstep(uDarkShineLo, uDarkShineHi, Lm);
    } else {
      // LIGHT: the original. Brightest art foils.
      shine = smoothstep(uShineLo, uShineHi, Lm);
    }
    // Add the subject's MACRO 3D form from the baked depth map (Depth-Anything
    // V2) on top of the foilmap's micro relief. The depth gradient is the true
    // surface slope, so folding it into (lx, ly) makes every specular / env /
    // iridescent highlight wrap the subject's real shape. The foil reads as a
    // sculpted 3D surface, not a flat sheet. WITHOUT moving any art pixel. A
    // 2-texel central difference (depth is smooth, so a wide step stays clean).
    if (uHasDepthMap > 0.5) {
      vec2 ds = uTexel * 2.0;
      float dxp = texture2D(uDepthMap, puv + vec2(ds.x, 0.0)).r;
      float dxm = texture2D(uDepthMap, puv - vec2(ds.x, 0.0)).r;
      float dyp = texture2D(uDepthMap, puv + vec2(0.0, ds.y)).r;
      float dym = texture2D(uDepthMap, puv - vec2(0.0, ds.y)).r;
      lx += (dxp - dxm) * uDepthReliefStrength;
      ly += (dyp - dym) * uDepthReliefStrength;
    }
    vec3 N = normalize(vec3(-lx * uReliefStrength, -ly * uReliefStrength, 1.0));
    float relief = clamp(length(vec2(lx, ly)) * 7.0, 0.0, 1.0);

    vec2 ip = imgSpace(puv);
    float dist = min(zoneSDF(ip, uZone0), zoneSDF(ip, uZone1));
    float bevel = smoothstep(0.0, -0.02, dist) * (1.0 - smoothstep(-0.02, -0.05, dist)) * uEmbossOn;
    float zoneHere = smoothstep(0.004, -0.02, dist);
    // Inside the text zones, allow the foil on the near-white substrate AND on
    // the embossed bevel edge (so the raised frame is iridescent too, not just
    // embossed), while still keeping it off the dark text. Outside the zones
    // this is 1.0 (art foil unchanged).
    float zoneFoil = mix(1.0, max(whiteMask, bevel), zoneHere);
    N = normalize(N + vec3(0.0, 0.0, bevel * 0.8));

    // V / Lr / H drive all of the specular, env reflection, iridescence and
    // sparkle below. Two paths, selected by uSceneLit:
    //   DOM (uSceneLit=0): the EXISTING screen-space self-lit model. Fixed
    //     head-on view, a synthetic light position from uTilt + rest light.
    //   Board (uSceneLit=1): the REAL tangent-space camera + scene light, so
    //     the highlight tracks the camera as it orbits and the scene's day/
    //     night light direction.
    // lightXY is the shared 2D "light position" the holo / env / sparkle terms
    // key off; on the board we synthesize it from the light's tangent-space XY
    // so those rainbow/flake terms still sweep, plus a small view term so the
    // hue shifts as the camera orbits.
    vec2 lightXY;
    float glareGuard;
    vec3 V, Lr, H;
    if (uSceneLit > 0.5) {
      V = normalize(vViewTS);
      Lr = normalize(vLightTS);
      H = normalize(V + Lr);
      // Hue/flake/env sweep driven by the REAL view direction (camera orbit)
      // blended with the light direction, instead of the synthetic uTilt.
      lightXY = V.xy * 1.1 + Lr.xy * 0.5;
      // NO head-on suppression on the board: cards lie flat and are viewed
      // near-normal (high V.z), so the DOM straight-on glare guard would kill
      // the foil at the typical table angle (it looked like a plain card). The
      // energy-bounded composite already prevents a white-out, so keep full.
      glareGuard = 1.0;
    } else {
      // Effective light = tilt + resting light position (so a flat card still
      // catches light from a chosen angle).
      lightXY = uTilt + vec2(uRestLightX, uRestLightY);
      // Straight-on glare guard: when the light hits dead-on (lightXY ~ 0) the
      // half-vector aligns with nearly every near-vertical relief normal at once,
      // so the broad specular + reflection wash the whole card grey. Fade those
      // two layers to ~0 in a TINY zone around lightXY=0 so that worst angle
      // becomes a brief, barely-noticeable dim instead of an ugly grey wash.
      glareGuard = smoothstep(0.0, 0.12, length(lightXY));
      V = vec3(0.0, 0.0, 1.0);
      Lr = normalize(vec3(lightXY * 2.2, 1.0));
      H = normalize(Lr + V);
    }
    float ndh = max(dot(N, H), 0.0);
    float ndl = max(dot(N, Lr), 0.0);
    float ndv = max(dot(N, V), 0.0);
    float vdh = max(dot(V, H), 0.0);

    float D = D_GGX(ndh, uRough);
    float G = G_Smith(ndl, ndv, uRough);
    vec3 F0 = vec3(0.95, 0.93, 0.88);
    vec3 F = F0 + (1.0 - F0) * pow(1.0 - vdh, 5.0);
    vec3 ggx = (D * G) * F / (4.0 * max(ndv, 0.02) * max(ndl, 0.02) + 1e-3) * ndl;
    ggx = min(ggx, vec3(2.5));

    // Env reflection direction. DOM: the tilt-swept normal model. Board: the
    // true reflection of the view vector about the relief normal, so the studio
    // env genuinely mirrors what the camera sees as it orbits.
    vec3 reflTS = reflect(-V, N);
    vec2 envDir = (uSceneLit > 0.5) ? reflTS.xy : (N.xy * 0.9 + lightXY * 1.6);
    vec3 env;
    if (uMatcapOn > 0.5) {
      // Real environment reflection: map the tilt-swept normal into the studio
      // matcap. Classic matcap UV is N.xy * 0.5 + 0.5; we add the tilt so the
      // studio sweeps as the card moves, then rotate the lookup by uMatcapRot.
      vec2 refl = (uSceneLit > 0.5) ? reflTS.xy : (N.xy + lightXY * 0.6);
      float cr = cos(uMatcapRot);
      float sr = sin(uMatcapRot);
      vec2 rot = vec2(refl.x * cr - refl.y * sr, refl.x * sr + refl.y * cr);
      vec2 muv = clamp(rot * 0.5 + 0.5, 0.0, 1.0);
      env = texture2D(uMatcap, muv).rgb * F;
    } else {
      env = studioEnv(envDir) * F;
    }

    float a = dot(H, N) * 2.5 + uv0.y * 1.2 + uFromCenter * 0.8;
    vec3 irid;
    if (uThinFilmOn > 0.5) {
      float cosTheta = clamp(dot(V, H), 0.0, 1.0);
      irid = thinFilm(cosTheta, uFilmThickness, uFilmIor);
    } else {
      irid = metallicRamp(a);
    }
    vec3 tint = mix(vec3(1.0), irid, uIridTint);

    float cellSize = uTexel.x * uSparkleDensity;
    vec2 cellId = floor(puv / cellSize);
    vec2 cf = fract(puv / cellSize) - 0.5;
    float r1 = hash(cellId);
    float r2 = hash(cellId + 19.7);
    vec2 flakeDir = normalize(vec2(r1, r2) * 2.0 - 1.0);
    vec2 td = lightXY / (length(lightXY) + 1e-3);
    float align = dot(td, flakeDir);
    float pointShape = 1.0 - smoothstep(0.0, 0.16, length(cf));
    float flash = smoothstep(0.82, 1.0, align) * step(uSparkleThresh, r1) * pointShape;

    // Multiscale microfacet glint (Chermain 2020 / Zirr 2016 inspired, but a
    // procedural slope-matching approximation, NOT a precomputed dictionary).
    // A surface microfacet glints when its slope matches the half-vector slope.
    // We project H to slope space, then over a few octaves of cell grids draw a
    // random microfacet slope per cell and add a sharp lobe weighted by how
    // closely that slope matches hSlope. Because hSlope comes from H (which
    // already folds in the rest light + tilt), glints twinkle as light/view
    // change, not merely when tilt aligns to a fixed flake direction.
    vec2 hSlope = H.xy / max(H.z, 1e-3);
    float glint = 0.0;
    float baseCell = 1.0 / max(uGlintDensity, 1.0);
    const int GLINT_OCTAVES = 3;
    float octWeight = 0.0;
    for (int o = 0; o < GLINT_OCTAVES; o++) {
      float scale = pow(2.0, float(o));
      float cellSizeOct = baseCell / scale;
      vec2 gid = floor(puv / cellSizeOct) + vec2(float(o) * 37.0, float(o) * 53.0);
      // Random microfacet slope for this cell, spread ~ roughness.
      vec2 mSlope = (hash2(gid) * 2.0 - 1.0) * uGlintSpread;
      vec2 d = hSlope - mSlope;
      float lobe = exp(-uGlintSharp * dot(d, d));
      // Per-cell random gate keeps glints sparse / discrete (sparkle look).
      float gate = step(0.6, hash(gid + 11.3));
      // Finer octaves contribute a little less so coarse glints dominate.
      float w = 1.0 / scale;
      glint += lobe * gate * w;
      octWeight += w;
    }
    glint /= max(octWeight, 1e-3);

    // DOM gates the foil intensity by tilt magnitude (motion only on tilt). The
    // board has no per-card tilt. Drive it from the specular alignment instead
    // (how close the half-vector is to the surface normal) so the foil flares
    // as the highlight crosses the card during a camera orbit, then sits at the
    // rest floor otherwise.
    float master = (uSceneLit > 0.5)
      ? clamp(ndh * 1.6, 0.0, 1.0)
      : clamp(length(uTilt) * 2.2, 0.0, 1.0);
    float intensity = max(master, uRestFloor);

    vec3 foil = vec3(0.0);
    // Tint the metal reflection toward a vivid spatial rainbow (uMetalTint) so
    // the metal shimmers colour like the deck-builder holo instead of reading
    // as neutral silver. Uses a saturation-boosted cosine rainbow that varies
    // across the card + with the half-vector (so it shifts as you tilt),
    // independent of the realistic thin-film. 0 = silver, 1 = full rainbow.
    // Deck-builder holo, faithfully: the 5-colour ramp along a 110-degree
    // axis, ~half a cycle across the card, slid by the tilt POSITION (no
    // view-angle term. That is what kept the legacy smooth, not flickery).
    // At rest (uTilt = 0) this is the static rainbow gradient the deck builder
    // shows; tilting slides it.
    float holoAxis = uv0.x * 0.94 + uv0.y * 0.34;
    // DOM slides the rainbow by tilt POSITION; board slides it by the view
    // direction so the hue shifts as the camera orbits the table.
    float holoSlide = (uSceneLit > 0.5)
      ? (V.x * 0.94 + V.y * 0.34) * uHueShift
      : (uTilt.x * 0.94 + uTilt.y * 0.34) * uHueShift;
    float holoArg = holoAxis * 0.6 + holoSlide;
    // Depth-parallax the holographic layer: near pixels (high depth) slide the
    // rainbow further than far pixels, so the sheen reads as a refractive film
    // floating ABOVE the art and shifting with tilt. Depth lives in the shine,
    // not in moved pixels. Keyed to the same direction as holoSlide (tilt on
    // DOM, view on the board). No effect without a baked depth map.
    if (uHasDepthMap > 0.5) {
      float depthShine = texture2D(uDepthMap, puv).r - 0.5;
      vec2 shineDir = (uSceneLit > 0.5) ? V.xy : uTilt;
      holoArg += depthShine * (shineDir.x * 0.94 + shineDir.y * 0.34) * uDepthShine;
    }
    // Default: the smooth, metallic cosine palette. uDeckRamp swaps in the
    // vivid 5-colour deck-builder ramp for an exact-match A/B.
    vec3 metalRainbow = (uDeckRamp > 0.5) ? rainbow5(holoArg) : satBoost(metallicRamp(holoArg), 1.7);
    foil += env * shine * uEnvStr * uEnvOn * glareGuard;
    foil += ggx * tint * (0.85 * relief + 0.55 * shine) * uGgxStr * uGgxOn * glareGuard;
    foil += irid * shine * uIridStr * uIridOn;
    // Two INDEPENDENT sparkle layers, each its own toggle: legacy hashed
    // (uSparkleOn) and the half-vector microfacet glint (uGlintOn).
    foil += vec3(1.0) * flash * shine * uSparkleStr * uSparkleOn;
    foil += vec3(1.0) * glint * shine * uGlintBrightness * uGlintOn;
    // Metallic layers above are restricted to the white substrate inside the
    // text zones so the lettering stays crisp.
    foil *= zoneFoil;
    // Emboss is STRUCTURAL (the raised box edge). Applied after the white-mask
    // gate so it is not killed by it, and not gated by shine.
    foil += ggx * bevel * uEmbossStr;
    foil *= intensity;

    float shadow = max(-dot(N, Lr), 0.0) * bevel * master * uShadowStr;

    // Always-on rest signature: a static spatial holo film on the light areas
    // (NOT tilt-gated), so the card reads as foil even when flat. Dark areas
    // (low shine) are untouched, so text stays clean.
    // Rest sheen uses the SAME deck-builder rainbow (static gradient at rest),
    // on the light areas, so the resting colours match the deck builder too.
    vec3 restSheen = metalRainbow * shine * uRestHolo;

    // Energy-bounded combine. The additive foil layers blow the whole card to
    // white at the specular peak (looks cheap), so: Reinhard-compress the foil
    // (asymptotes below 1) then SCREEN it over the base. Screen never exceeds
    // white AND adds little where the base is already bright, so the art stays
    // legible at every angle instead of clipping to a flat white wash.
    vec3 baseShadowed = base * (1.0 - shadow);
    vec3 f = (foil + restSheen);
    f = f / (1.0 + f);
    vec3 outc = baseShadowed + f * (1.0 - baseShadowed);
    // Hue-filter the light/metal areas toward the spatial rainbow (deck-builder
    // colour-dodge style): recolour the bright areas so the metal shows real
    // hues instead of staying silver. Dark art (low shine) is untouched.
    // metalTint = amount; the rainbow varies across the card + shifts on tilt.
    // Iridescent sheen on the LIGHT parts only (like real Sorcery foils):
    // gated by shine, so the lit fur / bright areas get a metallic colour
    // shimmer while the mid-tone subject keeps its own hue (fox stays orange)
    // and the dark background stays matte. It's a CHROMA add (rainbow minus
    // grey), not a recolour, so it preserves the art. Dark text kept out via
    // zoneFoil.
    vec3 holoChroma = (metalRainbow - 0.5) * luma(outc);
    outc += holoChroma * uMetalTint * zoneFoil * shine * 1.8;

    // White (#FFFFFF) parts. Text-box strokes, the card name, white art
    // highlights. Can't gain hue from an additive sheen (already maxed), so
    // RECOLOUR them toward the rainbow: white reads as metallic colour foil.
    // Gated by whiteMask so coloured art (fox body) is untouched. Confined to
    // LIGHT-mode cards (foilMode < 0.33): on DARK/COLOUR cards the foil target
    // isn't the white substrate, so this white recolour must NOT also fire. That
    // double-foiling (mode shine + white accents here) is the "both applied" bug.
    float lightModeOnly = 1.0 - step(0.33, foilMode);
    outc = mix(outc, outc * metalRainbow * 1.8, clamp(whiteMask * uMetalTint * 1.5 * lightModeOnly, 0.0, 1.0));

    // Board only: fold in the scene's lighting so the foil dims / warms with
    // the day-night cycle like its neighbouring lit cards. A simple wrapped
    // diffuse term on the relief normal (uLightColor) plus the flat ambient,
    // applied multiplicatively over the self-lit foil composite. uSceneLit=0
    // leaves the DOM look untouched.
    if (uSceneLit > 0.5) {
      float diffuse = clamp(dot(N, Lr) * 0.5 + 0.5, 0.0, 1.0);
      vec3 sceneShade = uAmbient + uLightColor * diffuse;
      outc *= sceneShade;
    }

    float alpha = 1.0;
    // uUseTexAlpha: take the card art's own alpha (its baked rounded-corner
    // transparency) as the output alpha. Used where the foil sits on a flat,
    // sharp quad with no rounded geometry to follow (the cemetery pile top face),
    // so its corners round EXACTLY like the non-foil card art instead of a
    // square opaque rect. Default 0 (opaque) everywhere else.
    if (uUseTexAlpha > 0.5) {
      alpha = texture2D(uTex, puv).a;
    } else if (uCornerRadius > 0.0) {
      // Rounded-rect coverage on vUv, measured in width units (y scaled by
      // the quad aspect) so the corner is circular on screen. uCornerRadius is
      // a fraction of the quad width; AA over a ~1.5px-ish band via fwidth.
      vec2 p = (vUv - 0.5) * vec2(1.0, uQuadAspect);
      vec2 q = abs(p) - (vec2(0.5, 0.5 * uQuadAspect) - uCornerRadius);
      float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uCornerRadius;
      float aa = max(fwidth(d), 1e-4);
      alpha = 1.0 - smoothstep(0.0, aa, d);
    }
    vec3 outFinal = clamp(outc, 0.0, 1.0);
    // Board parity (uOutputLinear=1): the foil math runs on raw sRGB-byte values
    // (uTex sampled NoColorSpace, like the deck builder). The scene renderer
    // encodes linear->sRGB on output, so we sRGB-DECODE here to cancel that
    // encode and put the exact deck-builder result on screen. DOM callers leave
    // uOutputLinear=0 (their renderer outputs LinearSRGB, no encode to cancel).
    if (uOutputLinear > 0.5) {
      outFinal = mix(outFinal / 12.92, pow((outFinal + 0.055) / 1.055, vec3(2.4)), step(0.04045, outFinal));
    }
    gl_FragColor = vec4(outFinal, alpha * uOpacity);
  }
`;
